import React, { useCallback, useMemo, useState } from 'react';
import { X, Plus, AlertTriangle } from 'lucide-react';
import { DeviceTag } from '../types';
import Button from './ui/Button';
import ConfirmDialog from './ConfirmDialog';
import { Combobox, ComboboxInlineAnchor, ComboboxInlineContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from './pilot-ui/combobox';
import {
  attachTag,
  detachTag,
  TagNeedsConfirmation,
  TagTooSimilar,
} from '../hooks/useDeviceTags';
import {
  findNearMisses,
  isExistingTag,
  normalizeTagName,
  rankTags,
  tagNameError,
} from '../utils/deviceTags';

/**
 * FS-501 — the one place a device tag can be attached, and therefore the one
 * place a tag can be created.
 *
 * Preventing tag proliferation is the reason this feature exists, so the
 * component is built around a single rule: an existing tag is always easier to
 * pick than a new one is to make.
 *
 *  - Matches are fuzzy and ranked, so `laun` surfaces `laundromat` before the
 *    operator can invent `laundry`.
 *  - Every suggestion carries its device count, so a one-off tag reads as the
 *    mistake it probably is.
 *  - Enter picks the highlighted existing tag. Enter NEVER creates — with no
 *    match highlighted it does nothing at all.
 *  - Creating is a separate, visually distinct action below a divider, and it
 *    opens a confirmation that names any near-miss existing tag first.
 *
 * The server enforces the same gate (409 `tag_create_unconfirmed`), so a
 * direct API call cannot skip it. The catch below turns that refusal back into
 * the same confirmation panel rather than a raw error.
 *
 * The near-miss confirmation (409 `tag_similar_unconfirmed`) works the same
 * way, with one rule that matters: `confirm_similar` is sent ONLY on a retry
 * answering a near miss the SERVER named. Both refusals carry the server's own
 * list, computed against the live vocabulary. The cached vocabulary is a hint —
 * it renders the warning without a round trip — but it never decides the flag.
 * Setting the flag from the cache would skip the server's check on exactly the
 * common path, so a tag another operator created since this cache loaded would
 * never be surfaced at all.
 */

interface Props {
  deviceId: number;
  /** Tags currently on the device. */
  tags: DeviceTag[];
  /** The whole vocabulary, for typeahead + counts. */
  vocabulary: DeviceTag[];
  /** Called after any change so the parent can refetch both lists. */
  onChanged: () => void;
  /** False when the operator lacks write_devices — chips render read-only. */
  canEdit: boolean;
}

const chipClass =
  'inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-sm border bg-slate-50 border-border text-slate-700 text-xs';
const CREATE_TAG_OPTION = '\u0000create-tag';

const TagEditor: React.FC<Props> = ({ deviceId, tags, vocabulary, onChanged, canEdit }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Non-null while the operator is confirming creation of this name.
   *
   * `serverRefusedSimilar` is true only once the SERVER has named a near miss
   * in a refusal, and it is the only thing that puts confirm_similar on the
   * request. `similar` may instead hold the cached hint, which is shown but
   * never confirmed — otherwise the client's own guess would wave the request
   * past the server's live check.
   */
  const [confirming, setConfirming] = useState<
    { name: string; similar: string[]; serverRefusedSimilar: boolean } | null
  >(null);

  const attached = useMemo(() => new Set(tags.map((t) => t.name)), [tags]);

  /**
   * Matches from the whole vocabulary, INCLUDING tags already on this device.
   *
   * Hiding the attached ones made the typeahead look broken and defeated its
   * purpose: on a device that already carries `laundromat`, typing `laun`
   * showed nothing at all, so the operator had no signal the tag existed and
   * was one keystroke from inventing `laundrmat` beside it. They are shown
   * and marked instead — seeing the tag is the point; attaching it again is
   * just the no-op we still avoid.
   */
  const suggestions = useMemo(() => rankTags(query, vocabulary), [query, vocabulary]);

  const normalized = normalizeTagName(query);
  const nameError = query.trim() ? tagNameError(query) : null;
  const exists = isExistingTag(query, vocabulary);
  const canOfferCreate = !!normalized && !exists && !nameError;


  const reset = useCallback(() => {
    setQuery('');
    setConfirming(null);
    setError(null);
    setOpen(false);
  }, []);

  const doAttach = useCallback(
    async (name: string, opts: { createNew?: boolean; confirmSimilar?: boolean } = {}) => {
      setBusy(true);
      setError(null);
      try {
        await attachTag(deviceId, name, opts);
        reset();
        onChanged();
      } catch (err: any) {
        if (err instanceof TagNeedsConfirmation) {
          // Server-side create gate fired. Its `similar` list is the server's
          // own near-miss result against the live vocabulary, computed before
          // this refusal was raised (tagsController.ts) — not an echo of the
          // cache. So a non-empty list here IS the near-miss warning, and
          // answering it confirms both gates in one retry rather than making
          // the operator click through a second, identical refusal.
          setConfirming({
            name: err.tagName,
            similar: err.similar,
            serverRefusedSimilar: err.similar.length > 0,
          });
        } else if (err instanceof TagTooSimilar) {
          // The server's own near-miss refusal, checked against the live
          // vocabulary — including tags this client has never seen. Show the
          // SERVER's list; the operator's next Create click is the answer to
          // this refusal, and only that retry may carry confirm_similar.
          setConfirming({ name: err.tagName, similar: err.similar, serverRefusedSimilar: true });
        } else {
          setError(err?.response?.data?.message || err?.message || 'Failed to attach tag');
        }
      } finally {
        setBusy(false);
      }
    },
    [deviceId, onChanged, reset],
  );

  const doDetach = useCallback(
    async (tag: DeviceTag) => {
      setBusy(true);
      setError(null);
      try {
        await detachTag(deviceId, tag.id);
        onChanged();
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to remove tag');
      } finally {
        setBusy(false);
      }
    },
    [deviceId, onChanged],
  );

  /** Open the confirmation panel, naming any tag this is one typo away from. */
  const askToCreate = useCallback(() => {
    if (!canOfferCreate) return;
    setConfirming({
      name: normalized,
      // Advisory only — computed from the cached vocabulary so the warning
      // appears without a round trip. It is a hint, never a confirmation: the
      // request goes out unconfirmed and the server checks live data, so a
      // near miss missing from this list still stops the create.
      similar: findNearMisses(
        normalized,
        vocabulary.map((t) => t.name),
      ),
      serverRefusedSimilar: false,
    });
  }, [canOfferCreate, normalized, vocabulary]);

  if (!canEdit) {
    return (
      <div className="flex flex-wrap gap-1">
        {tags.length === 0 ? (
          <span className="text-muted-foreground text-xs">No tags</span>
        ) : (
          tags.map((t) => (
            <span key={t.id} className={chipClass.replace(' pr-1', ' pr-2')}>
              {t.name}
            </span>
          ))
        )}
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="tag-editor-input" className="mb-1 block text-xs font-medium">Add a tag to this device</label>
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <span key={t.id} className={chipClass}>
            {t.name}
            <Button
              variant="icon"
              size="sm"
              className="size-6 shrink-0"
              disabled={busy}
              onClick={() => void doDetach(t)}
              aria-label={`Remove tag ${t.name}`}
              title={`Remove tag ${t.name} from this device`}
            >
              <X size={12} aria-hidden />
            </Button>
          </span>
        ))}
        <Combobox<string>
          items={[...suggestions.map((tag) => tag.name), ...(canOfferCreate ? [CREATE_TAG_OPTION] : [])]}
          filter={null}
          modal={false}
          value={null}
          inputValue={query}
          onInputValueChange={(next, details) => {
            if (details.reason === 'item-press') return;
            setQuery(next);
            setOpen(true);
          }}
          open={open && !confirming}
          onOpenChange={setOpen}
          onValueChange={(name) => {
            if (name === CREATE_TAG_OPTION) {
              askToCreate();
              return;
            }
            if (name && !attached.has(name)) void doAttach(name);
          }}
        >
          <ComboboxInlineAnchor onClose={() => setOpen(false)} className="min-w-36 flex-1">
          <ComboboxInput
            id="tag-editor-input"
            disabled={busy}
            showTrigger={false}
            onFocus={() => setOpen(true)}
            placeholder={tags.length === 0 ? 'Add a tag…' : 'Add another…'}
            aria-label="Add a tag to this device"
            aria-describedby={`tag-editor-help${nameError && query.trim() !== '' ? ' tag-editor-name-error' : ''}`}
            aria-invalid={!!(nameError && query.trim() !== '') || undefined}
            title="Find an existing tag by name; Enter attaches it. Creating a new tag needs separate confirmation."
            className="min-w-36 flex-1"
          />
          <ComboboxInlineContent open={open && !confirming} className="w-72">
            <ComboboxEmpty className="flex">{vocabulary.length === 0 ? 'No tags exist yet.' : 'No matching tags.'}</ComboboxEmpty>
            <ComboboxList aria-label="Matching tags">
              {(name: string) => {
                if (name === CREATE_TAG_OPTION) {
                  return (
                    <ComboboxItem key={name} value={name} title="Confirm creation of a new tag and attach it to this device">
                      <Plus size={12} aria-hidden /> Create new tag: <span className="font-semibold">{normalized}</span>
                    </ComboboxItem>
                  );
                }
                const tag = suggestions.find((item) => item.name === name);
                if (!tag) return null;
                const already = attached.has(name);
                return (
                  <ComboboxItem key={tag.id} value={name} disabled={busy || already} title={already ? 'Already on this device' : `Attach ${name}`}>
                    <span className="flex-1 truncate">{name}</span>
                    <span className="text-xs text-muted-foreground">
                      {already ? 'already on this device' : `${tag.device_count ?? 0} devices`}
                    </span>
                  </ComboboxItem>
                );
              }}
            </ComboboxList>
          </ComboboxInlineContent>
          </ComboboxInlineAnchor>
        </Combobox>
      </div>
      <p id="tag-editor-help" className="mt-1 text-xs text-muted-foreground">
        Choose an existing tag, or use Create new tag for a separate confirmation. Changes save immediately.
      </p>

      {nameError && query.trim() !== '' && (
        <p id="tag-editor-name-error" className="mt-1 text-xs text-amber-700">{nameError}</p>
      )}
      {error && !confirming && <p className="mt-1 text-xs text-red-700" role="alert">{error}</p>}

      {confirming && (
        <ConfirmDialog
          open
          title="Create tag"
          confirmLabel={confirming.serverRefusedSimilar ? `Create “${confirming.name}” anyway` : `Create “${confirming.name}”`}
          confirmTitle="Make this new tag and put it on this device"
          submitting={busy}
          error={error}
          onConfirm={() => void doAttach(confirming.name, {
            createNew: true,
            confirmSimilar: confirming.serverRefusedSimilar,
          })}
          onCancel={() => setConfirming(null)}
        >
          {confirming.similar.length > 0 ? (
            <>
              <p className="flex items-start gap-1.5 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-px shrink-0" aria-hidden />
                <span>
                  Very similar to{' '}
                  <span className="font-semibold">{confirming.similar[0]}</span>
                  {confirming.similar.length > 1 && (
                    <> (and {confirming.similar.length - 1} other)</>
                  )}
                  . Did you mean that one?
                </span>
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {confirming.similar.slice(0, 3).map((s) => (
                  <Button
                    key={s}
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => void doAttach(s)}
                    title={`Put the existing tag ${s} on this device instead of making a new one`}
                  >
                    Use {s}
                  </Button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-700">
              Create <span className="font-semibold">{confirming.name}</span> as a new tag?
            </p>
          )}

        </ConfirmDialog>
      )}
    </div>
  );
};

export default TagEditor;
